import { FC, useEffect, useState } from 'react';

import { chevronDown } from '@purpur/library/icon/assets/chevron-down';
import { chevronUp } from '@purpur/library/icon/assets/chevron-up';

import { useComponentId } from '@telialt/utils/src/hooks';

import { PLAN_CARD_TEST_ID } from '../constants';
import { useDescriptionModalControls } from '../hooks/useDescriptionModalControls';
import { PlanCardModel } from '../model';
import {
    ExpandableList,
    ExpandableListContainer,
    ExpandableListItem,
    ExpandableListItemDetail,
    ExpandableListTitle,
    ExpandButton,
    ExpandIcon,
} from '../PlanCard.styles';
import { DescriptionModalTriggerButton } from './DescriptionModalTriggerButton';

type Props = {
    title: string;
    descriptions: PlanCardModel.Description[];
    isForcedExpanded?: boolean;
    onExpandChange?: (isExpanded: boolean) => void;
};

export const DescriptionList: FC<Props> = ({ title, descriptions, isForcedExpanded, onExpandChange }) => {
    const UID = useComponentId('card-expandable-list');
    const ID = { TITLE: `${UID}-title`, LIST: `${UID}-list` };
    const [isExpanded, setIsExpanded] = useState(true);
    const { openDescriptionModal } = useDescriptionModalControls();

    const handleExpandClick = () => {
        setIsExpanded(!isExpanded);
        onExpandChange?.(!isExpanded);
    };

    useEffect(() => {
        if (typeof isForcedExpanded === 'boolean') {
            setIsExpanded(isForcedExpanded);
        }
    }, [isForcedExpanded]);

    if (!descriptions?.length) {
        return null;
    }

    return (
        <>
            <ExpandButton onClick={handleExpandClick} aria-controls={ID.LIST} aria-expanded={isExpanded}>
                {title}
                <ExpandIcon svg={isExpanded ? chevronUp : chevronDown} />
            </ExpandButton>
            <ExpandableListTitle id={ID.TITLE}>{title}</ExpandableListTitle>
            <ExpandableListContainer id={ID.LIST} $expanded={isExpanded}>
                <ExpandableList aria-labelledby={ID.TITLE} data-test={PLAN_CARD_TEST_ID.DESCRIPTION_LIST}>
                    {descriptions.map((description, index) => (
                        <ExpandableListItem key={index}>
                            <DescriptionModalTriggerButton
                                onClick={(event) => openDescriptionModal(description.id, event.currentTarget)}
                            >
                                {description.term}
                            </DescriptionModalTriggerButton>
                            <ExpandableListItemDetail>{description.detail}</ExpandableListItemDetail>
                        </ExpandableListItem>
                    ))}
                </ExpandableList>
            </ExpandableListContainer>
        </>
    );
};
